// Mobile Observe Player - Vertical Dropdown Layout (Updated)
import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { FlatList, Text, View, TouchableOpacity, Image, Modal, Animated } from "react-native";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { useLiveStream } from "../../context/LiveStreamContext";
import Avatar from "../choose-avatar-components/Avatar";
import { AppImages } from "../../utility/AppImages";
import {
  useAvatarPositions,
  useStreamState,
  useErrorHandler,
  useDeviceBreakpoints
} from "../../screens/Auth/WebScreens/OnGoingEvents/LiveStreaming/hooks";
import {
  MobileObservePlayerProps
} from "../../screens/Auth/WebScreens/OnGoingEvents/LiveStreaming/types";
import { ERROR_MESSAGES, ANIMATION_DELAYS } from "../../screens/Auth/WebScreens/OnGoingEvents/LiveStreaming/constants";

const MobileObservePlayer: React.FC<MobileObservePlayerProps> = ({
  playerData,
  selectedPlayer,
  setSelectedPlayer,
  setPlayerData,
  onAvatarLayout,
}) => {
  const { audienceData } = useLiveStream();
  const { showError } = useErrorHandler();
  const { isStreamingAvailable, isOrganiserStreamLive, isChallengerStreamLive } = useStreamState();
  const { isMobile } = useDeviceBreakpoints();
  const [isExpanded, setIsExpanded] = useState(false);
  const animatedHeight = useRef(new Animated.Value(0)).current;

  // Check if event has started - if event status is LIVE OR organiser/challenger is streaming
  const isEventLive = audienceData?.eventDetails?.status === "live";
  const eventHasStarted = isEventLive || isOrganiserStreamLive || isChallengerStreamLive;
  const {
    player1AvatarRef,
    player2AvatarRef,
    bothAvatarRef,
    measureAvatarPositions
  } = useAvatarPositions(onAvatarLayout);

  // Check if we're in mobile single-player mode
  const isMobileSinglePlayer = useMemo(() => {
    return isMobile && selectedPlayer !== "both";
  }, [isMobile, selectedPlayer]);

  useEffect(() => {
    if (audienceData?.participantDetails) {
      const transformedData = audienceData.participantDetails.map(
        (participant: any, index: number) => ({
          id: index + 1,
          userID: participant.userID,
          image: participant.profilePicture || "",
          name: participant.userID || "Unknown",
          type: `Player ${index + 1}`,
          watch: (participant.userID === selectedPlayer ? "watching" : "watch") as "watching" | "watch",
        })
      );

      const combinedPlayerData = {
        id: transformedData.length + 1,
        userID: "both",
        image: "",
        name: "Both Players",
        type: "Combined",
        watch: "watch" as const,
        isCombined: true,
      };

      setPlayerData([...transformedData, combinedPlayerData]);

      // Set initial player: if event hasn't started on mobile, select first player, otherwise keep current selection
      if (!selectedPlayer && transformedData.length > 0) {
        if (isMobile && !eventHasStarted) {
          setSelectedPlayer(transformedData[0].userID);
        } else if (!isMobile) {
          setSelectedPlayer(transformedData[0].userID);
        }
      }
    }
  }, [audienceData, selectedPlayer, setPlayerData, setSelectedPlayer, isMobile, eventHasStarted]);

  useEffect(() => {
    if (isExpanded) {
      Animated.timing(animatedHeight, {
        toValue: 1,
        duration: 300,
        useNativeDriver: false,
      }).start();
    } else {
      Animated.timing(animatedHeight, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false,
      }).start();
    }
  }, [isExpanded]);

  const Player1 = playerData[0] || { name: "", image: "" };
  const Player2 = playerData[1] || { name: "", image: "" };

  // Get header title based on selected player
  const getHeaderTitle = () => {
    if (selectedPlayer === "both") {
      return "Observe Player";
    }
    const selectedPlayerData = playerData.find(p => p.userID === selectedPlayer);
    if (selectedPlayerData && !selectedPlayerData.isCombined) {
      return `Observe ${selectedPlayerData.name}`;
    }
    return "Observe player";
  };

  const renderDropdownItem = useCallback(({ item, index }: { item: any; index: number }) => {
    if (item.isCombined) {
      const player1 = playerData[0] || { name: "Player 1", image: "" };
      const player2 = playerData[1] || { name: "Player 2", image: "" };

      return (
        <TouchableOpacity
          onPress={() => {
            setSelectedPlayer("both");
            setIsExpanded(false);
          }}
          style={{
            backgroundColor: selectedPlayer === "both" ? "rgba(220, 38, 38, 0.2)" : "#111827",
            borderColor: "#374151",
            borderWidth: 1,
            borderRadius: 8,
            marginHorizontal: 16,
            marginBottom: isMobileSinglePlayer ? 6 : 8,
            padding: isMobileSinglePlayer ? 12 : 16,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {/* First Player */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              flex: 1,
            }}
          >
            <View
              ref={bothAvatarRef}
              style={{
                borderRadius: 100,
                borderColor: AppColors.koBrandRed,
                borderWidth: 2,
                width: 50,
                height: 50,
                marginRight: 12,
              }}
              onLayout={() => setTimeout(measureAvatarPositions, ANIMATION_DELAYS.POSITION_MEASUREMENT)}
            >
              <Avatar src={player1.image} variant="player" />
            </View>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_16,
                { color: "white" },
              ]}
              numberOfLines={1}
            >
              {player1.name}
            </Text>
          </View>

          {/* Center Watching Button */}
          <View
            style={{
              backgroundColor: selectedPlayer === "both" ? AppColors.koBrandRed : AppColors.koBrandDarkGray,
              borderRadius: 8,
              paddingVertical: 8,
              paddingHorizontal: 16,
              marginHorizontal: 12,
            }}
          >
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_12,
                { color: "white" },
              ]}
            >
              {selectedPlayer === "both" ? "Watching" : "Watch"}
            </Text>
          </View>

          {/* Second Player */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_16,
                { color: "white" },
              ]}
              numberOfLines={1}
            >
              {player2.name}
            </Text>
            <View
              style={{
                borderRadius: 100,
                borderColor: AppColors.koBrandRed,
                borderWidth: 2,
                width: 50,
                height: 50,
                marginLeft: 12,
              }}
            >
              <Avatar src={player2.image} variant="player" />
            </View>
          </View>
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity
        onPress={() => {
          if (isStreamingAvailable) {
            setSelectedPlayer(item.userID);
            setIsExpanded(false);
          } else {
            showError(ERROR_MESSAGES.STREAMING_NOT_AVAILABLE);
          }
        }}
        style={{
          backgroundColor: item.userID === selectedPlayer ? "rgba(220, 38, 38, 0.2)" : "#111827",
          borderColor: "#374151",
          borderWidth: 1,
          borderRadius: 8,
          marginHorizontal: 16,
          marginBottom: isMobileSinglePlayer ? 6 : 8,
          padding: isMobileSinglePlayer ? 12 : 16,
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <View
          ref={item.userID === audienceData?.participantDetails?.[0]?.userID ? player1AvatarRef :
            item.userID === audienceData?.participantDetails?.[1]?.userID ? player2AvatarRef : null}
          style={{
            borderRadius: 100,
            borderColor: AppColors.koBrandRed,
            borderWidth: 2,
            width: 50,
            height: 50,
            marginRight: 16,
          }}
          onLayout={() => setTimeout(measureAvatarPositions, ANIMATION_DELAYS.POSITION_MEASUREMENT)}
        >
          <Avatar src={item.image} variant="player" />
        </View>
        <View style={{ flex: 1 }}>
          <Text
            style={[
              AppFontStyle.REGULAR_ANYBODY_16,
              { color: "white" },
            ]}
            numberOfLines={1}
          >
            {item.name}
          </Text>
        </View>
        <View
          style={{
            backgroundColor: item.userID === selectedPlayer ? AppColors.koBrandRed : AppColors.koBrandDarkGray,
            borderRadius: 8,
            paddingVertical: 8,
            paddingHorizontal: 16,
          }}
        >
          <Text
            style={[AppFontStyle.BOLD_ANYBODY_12, { color: "white" }]}
          >
            {item.watch}
          </Text>
        </View>
      </TouchableOpacity>
    );
  }, [isStreamingAvailable, selectedPlayer, setSelectedPlayer, playerData, audienceData]);

  // Completely hide the entire component on mobile before event starts
  const shouldHideComponent = isMobile && !eventHasStarted;

  // If event hasn't started on mobile, return null to completely hide
  if (shouldHideComponent) {
    return null;
  }

  return (
    <View style={{ marginHorizontal: 0, marginTop: 0, marginBottom: 0, zIndex: 2000, overflow: 'visible' }}>
      {/* Header with dropdown */}
      <TouchableOpacity
        onPress={() => setIsExpanded(!isExpanded)}
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          paddingVertical: 12,
          paddingHorizontal: 16,
          backgroundColor: "#484444",
          borderBottomWidth: 0.3,
          borderBottomColor: "rgba(139, 139, 139, 1)",
        }}
      >
        <Text style={[AppFontStyle.BOLD_ANYBODY_16, { color: "white" }]}>
          {getHeaderTitle()}
        </Text>
        <Image
          source={AppImages.arrowDown}
          style={{
            width: 16,
            height: 16,
            transform: [{ rotate: isExpanded ? "180deg" : "0deg" }],
          }}
          resizeMode="contain"
        />
      </TouchableOpacity>

      {/* Backdrop overlay */}
      <Animated.View
        style={{
          position: "absolute",
          top: 60,
          left: -15,
          right: -15,
          bottom: -500,
          zIndex: 2500,
          opacity: animatedHeight,
        }}
        pointerEvents={isExpanded ? "auto" : "none"}
      >
        <TouchableOpacity
          style={{ flex: 1 }}
          onPress={() => setIsExpanded(false)}
          activeOpacity={1}
        />
      </Animated.View>

      {/* Dropdown content - Card Layout */}
      <Animated.View
        style={{
          position: "absolute",
          top: 44, // Position exactly at the header's bottom border
          left: 12,
          right: 12,
          backgroundColor: "#111827",
          borderWidth: 1,
          borderColor: "#6B7280",
          borderTopWidth: 0, // Remove top border to connect with header
          paddingVertical: isMobileSinglePlayer ? 8 : 12,
          paddingHorizontal: 0,
          zIndex: 3000,
          elevation: 20, // For Android shadow
          shadowColor: "#000", // For iOS shadow
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.3,
          shadowRadius: 8,
          maxHeight: animatedHeight.interpolate({
            inputRange: [0, 1],
            outputRange: [0, isMobileSinglePlayer ? 250 : 300],
          }),
          opacity: animatedHeight,
          transform: [{
            scaleY: animatedHeight,
          }],
          transformOrigin: "top",
        }}
        pointerEvents={isExpanded ? "auto" : "none"}
      >
        <FlatList
          data={playerData}
          renderItem={renderDropdownItem}
          keyExtractor={(item) => `dropdown-${item.id}`}
          showsVerticalScrollIndicator={isMobileSinglePlayer}
          scrollEnabled={isMobileSinglePlayer ? true : playerData.length > 3}
          nestedScrollEnabled={false}
          contentContainerStyle={{ paddingBottom: isMobileSinglePlayer ? 4 : 8 }}
        />
      </Animated.View>
    </View>
  );
};

export default MobileObservePlayer;
